/* ============================================
   PWAInstallGate — Mobile PWA installation screen
   Shows when mobile user hasn't installed the PWA.
   Blocks access until installed (standalone mode).
   Desktop users pass through without seeing this.
   ============================================ */
import { useState, type ReactNode } from 'react'
import { usePWAInstall } from '@/hooks/usePWAInstall'

interface Props {
  children: ReactNode
}

export default function PWAInstallGate({ children }: Props) {
  const { needsInstall, canInstall, isIOS, justInstalled, promptInstall, ready } = usePWAInstall()
  const [showManualSteps, setShowManualSteps] = useState(false)

  // Still detecting environment → show nothing (avoids flash)
  if (!ready) return null

  // Desktop or already in standalone → pass through
  if (!needsInstall) return <>{children}</>

  // Detect if likely Chrome (supports beforeinstallprompt)
  const isChrome = /Chrome/i.test(navigator.userAgent) && !/Edge|OPR|Samsung/i.test(navigator.userAgent)

  // El portal del cliente vive en client./portal.: ahí "ERP" no le dice nada a
  // quien instala, así que el texto habla de lo suyo.
  const host = window.location.hostname
  const esPortalCliente = host.startsWith('client.') || host.startsWith('portal.')

  return (
    <div className="pwa-gate">
      {/* Animated background */}
      <div className="pwa-gate__bg">
        <div className="pwa-gate__orb pwa-gate__orb--1" />
        <div className="pwa-gate__orb pwa-gate__orb--2" />
        <div className="pwa-gate__orb pwa-gate__orb--3" />
      </div>

      <div className="pwa-gate__content">
        {/* Logo */}
        <div className="pwa-gate__logo-wrap">
          <img
            src="/assets/brand/pwa-icon-192.png"
            alt="1Conecta"
            className="pwa-gate__app-icon"
          />
        </div>

        <img
          src="/assets/brand/logo-full-white.svg"
          alt="1Conecta"
          className="pwa-gate__wordmark"
        />

        {/* ─── Success state: app was just installed ─── */}
        {justInstalled ? (
          <>
            <div className="pwa-gate__success-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#4F7FE8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h1 className="pwa-gate__title">¡App instalada!</h1>
            <p className="pwa-gate__subtitle">
              Ahora abre <strong>1Conecta</strong> desde tu pantalla de inicio para comenzar.
            </p>
            <div className="pwa-gate__success-hint">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              <span>Busca el ícono de 1Conecta en tu pantalla de inicio</span>
            </div>
          </>
        ) : (
          <>
            {/* ─── Install state ─── */}
            <h1 className="pwa-gate__title">Instala la App</h1>
            <p className="pwa-gate__subtitle">
              {esPortalCliente
                ? 'Instalá 1Conecta en tu celular para ver tus proyectos, facturas y apps desde la pantalla de inicio.'
                : 'Descarga 1Conecta en tu dispositivo para acceder al ERP desde tu celular.'}
            </p>

            {/* Android/Chrome: native install prompt available */}
            {canInstall && (
              <button
                className="pwa-gate__install-btn"
                onClick={promptInstall}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Instalar 1Conecta
              </button>
            )}

            {/* iOS: manual instructions */}
            {isIOS && (
              <div className="pwa-gate__ios">
                <p className="pwa-gate__ios-title">Sigue estos pasos:</p>
                <ol className="pwa-gate__ios-steps">
                  <li>
                    Toca el botón <strong>Compartir</strong>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4F7FE8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                      <polyline points="16 6 12 2 8 6" />
                      <line x1="12" y1="2" x2="12" y2="15" />
                    </svg>
                  </li>
                  <li>
                    Selecciona <strong>Agregar a pantalla de inicio</strong>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4F7FE8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <line x1="12" y1="8" x2="12" y2="16" />
                      <line x1="8" y1="12" x2="16" y2="12" />
                    </svg>
                  </li>
                  <li>
                    Toca <strong>Agregar</strong> para confirmar
                  </li>
                </ol>
              </div>
            )}

            {/* Fallback: no native prompt available and not iOS */}
            {!canInstall && !isIOS && (
              <div className="pwa-gate__fallback-full">
                {isChrome ? (
                  <>
                    {/* Chrome but prompt didn't fire — show manual steps */}
                    <p className="pwa-gate__fallback-heading">
                      Agrega la app a tu pantalla de inicio:
                    </p>
                    <ol className="pwa-gate__manual-steps">
                      <li>
                        <span className="pwa-gate__step-number">1</span>
                        <span>Toca el menú <strong>⋮</strong> (tres puntos) arriba a la derecha</span>
                      </li>
                      <li>
                        <span className="pwa-gate__step-number">2</span>
                        <span>Selecciona <strong>"Agregar a pantalla de inicio"</strong> o <strong>"Instalar app"</strong></span>
                      </li>
                      <li>
                        <span className="pwa-gate__step-number">3</span>
                        <span>Toca <strong>Instalar</strong> para confirmar</span>
                      </li>
                    </ol>
                  </>
                ) : (
                  <>
                    {/* Non-Chrome browser: recommend Chrome or show manual */}
                    <div className="pwa-gate__browser-notice">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4F7FE8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      <p>
                        Para la mejor experiencia, abre este enlace en <strong>Google Chrome</strong>.
                      </p>
                    </div>

                    {!showManualSteps ? (
                      <button
                        className="pwa-gate__alt-btn"
                        onClick={() => setShowManualSteps(true)}
                        type="button"
                      >
                        Instalar desde este navegador
                      </button>
                    ) : (
                      <ol className="pwa-gate__manual-steps">
                        <li>
                          <span className="pwa-gate__step-number">1</span>
                          <span>Abre el menú del navegador</span>
                        </li>
                        <li>
                          <span className="pwa-gate__step-number">2</span>
                          <span>Busca <strong>"Agregar a pantalla de inicio"</strong> o <strong>"Instalar"</strong></span>
                        </li>
                        <li>
                          <span className="pwa-gate__step-number">3</span>
                          <span>Confirma la instalación</span>
                        </li>
                      </ol>
                    )}
                  </>
                )}
              </div>
            )}

            <p className="pwa-gate__footer-note">
              La app se instalará sin ocupar espacio significativo en tu dispositivo.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
